<template>
  <Button v-bind="getBindValue" :loading="props.loading || loading" :class="getButtonClass">
    <template v-if="!!icon || typeof props.icon === 'string'" #icon="data">
      <slot name="icon" v-bind="data || {}">
        <LoadingOutlined v-if="props.loading || loading" spin />
        <MIcon
          v-else
          :prefix="props.iconPrefix"
          :icon="props.icon"
          :size="props.iconSize"
          class="mr-4px"
        />
      </slot>
    </template>

    <template v-for="slotName in Object.keys(slots)" #[slotName]="data">
      <slot :name="slotName" v-bind="data || {}"></slot>
    </template>
  </Button>
</template>

<script lang="ts" setup>
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import { Button } from 'ant-design-vue';
  import { ComponentOptionsMixin, computed, ref, useAttrs, useSlots } from 'vue';
  import { PromiseFn } from '@mts-fe/types';
  import { omit } from '@mts-fe/utils';
  import { MIcon } from '@mts/components/Icon';
  import { OMIT_ATTRS } from './const';
  import { MButtonProps } from './props';

  defineOptions({
    name: 'MButton',
    extends: Button as ComponentOptionsMixin,
    inheritAttrs: false,
  });

  const props = defineProps<MButtonProps>();
  const attrs = useAttrs();

  const getButtonClass = computed(() => {
    const classes = ['mts-btn'];
    if (props.color) {
      classes.push(`mts-btn-${props.color}`);
    }
    if (props.disabled) {
      classes.push('is-disabled');
    }
    return classes;
  });

  const { icon, ...slots } = useSlots();

  const loading = ref(false);
  const getBindValue = computed(() => {
    const omitPropsSet = new Set(['loading', 'color']);
    if (typeof props.icon === 'string') {
      omitPropsSet.add('icon').add('iconPrefix').add('iconSize');
    }

    const bindValue: Recordable = { ...omit(attrs, OMIT_ATTRS) };

    const propKeys = Object.keys(props) as Array<keyof typeof props>;
    for (const key of propKeys) {
      if (!omitPropsSet.has(key as string)) {
        bindValue[key] = props[key];
      }
    }

    const clickFn = props.onClick;
    if (clickFn && clickFn.constructor.name === 'AsyncFunction') {
      bindValue.onClick = async (...args: any[]) => {
        loading.value = true;
        try {
          await (clickFn as PromiseFn)(...args);
        } finally {
          loading.value = false;
        }
      };
    } else if (clickFn) {
      bindValue.onClick = clickFn;
    }

    return bindValue;
  });
</script>

<style lang="less">
  @colorMap: {
    green: @primary-color;
    success: @primary-color;
    yellow: #ffc168;
    warning: #ffc168;
    error: #ed6f6f;
    pink: #e872f7;
    blue: #5f75f7;
    purple: #a45efe;
    orange: #f78e6d;
    cyan: #00d4eb;
  };

  each(@colorMap, {
   .ant-btn.mts-btn-@{key}{
      &.ant-btn-primary:not(:disabled)  {
        background-color: @value;
      }
      &.ant-btn-primary:not(:disabled):hover {
        background-color: @value;
        filter: brightness(1.1);
      }
      &.ant-btn-default,
      &.ant-btn-default:hover {
        border-color: currentcolor;
        color: @value;
      }
      &.ant-btn-link,
      &.ant-btn-link:not(:disabled):hover{
        color: @value;
      }
    }
  });

  .ant-btn > .anticon {
    vertical-align: unset;
  }

  .mts-btn.ant-btn-icon-only {
    border: 0;
    box-shadow: none;

    > i {
      margin-right: 0;
    }
  }
</style>
